import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Users, UserPlus, Edit, Trash2, UserX, UserCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminEmployees() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'employee',
    department: '',
    designation: '',
    date_of_joining: '',
    salary: 0,
    weekly_off: ['Sunday'],
    paid_leaves_quota: 24,
    office_address: '',
    office_latitude: '',
    office_longitude: ''
  });

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data);
    } catch (error) {
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      if (editingEmployee) {
        // Update existing employee
        const updateData = {...formData};
        if (!updateData.password) {
          delete updateData.password;
        }
        await axios.put(`${API}/users/${editingEmployee.id}`, updateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Employee updated successfully');
      } else {
        // Create new employee
        await axios.post(`${API}/auth/register`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Employee added successfully');
      }
      
      fetchEmployees();
      setTimeout(() => {
        setDialogOpen(false);
        setEditingEmployee(null);
        setFormData({
          email: '',
          password: '',
          full_name: '',
          role: 'employee',
          department: '',
          designation: '',
          date_of_joining: '',
          salary: 0,
          weekly_off: ['Sunday'],
          paid_leaves_quota: 24,
          office_address: '',
          office_latitude: '',
          office_longitude: ''
        });
      }, 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save employee');
    }
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      email: employee.email,
      password: '',
      full_name: employee.full_name,
      role: employee.role,
      department: employee.department || '',
      designation: employee.designation || '',
      date_of_joining: employee.date_of_joining || '',
      salary: employee.salary || 0,
      weekly_off: Array.isArray(employee.weekly_off) ? employee.weekly_off : [employee.weekly_off || 'Sunday'],
      paid_leaves_quota: employee.paid_leaves_quota || 24,
      office_address: employee.office_address || '',
      office_latitude: employee.office_latitude || '',
      office_longitude: employee.office_longitude || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (employeeId, employeeName) => {
    if (!window.confirm(`Are you sure you want to permanently delete ${employeeName}? This will remove all their data including attendance and salary records.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/users/${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Employee deleted successfully');
      fetchEmployees();
    } catch (error) {
      toast.error('Failed to delete employee');
    }
  };

  const handleToggleActive = async (employeeId, currentStatus, employeeName) => {
    const action = currentStatus ? 'deactivate' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} ${employeeName}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/users/${employeeId}/toggle-active`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Employee ${action}d successfully`);
      fetchEmployees();
    } catch (error) {
      toast.error(`Failed to ${action} employee`);
    }
  };

  const handleDialogClose = (open) => {
    setDialogOpen(open);
    if (!open) {
      setEditingEmployee(null);
      setFormData({
        email: '',
        password: '',
        full_name: '',
        role: 'employee',
        department: '',
        designation: '',
        date_of_joining: '',
        salary: 0,
        weekly_off: ['Sunday'],
        paid_leaves_quota: 24,
        office_address: '',
        office_latitude: '',
        office_longitude: ''
      });
    }
  };

  return (
    <Layout>
      <div className="space-y-8" data-testid="admin-employees-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold tracking-tight">Employees</h1>
            <p className="text-muted-foreground mt-2">Manage employee information</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
            <DialogTrigger asChild>
              <Button data-testid="add-employee-button">
                <UserPlus className="mr-2 h-4 w-4" />
                Add Employee
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingEmployee ? 'Edit Employee' : 'Add New Employee'}</DialogTitle>
                <DialogDescription>
                  {editingEmployee ? 'Update employee details' : 'Fill in the employee details'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="full_name">Full Name *</Label>
                    <Input
                      id="full_name"
                      value={formData.full_name}
                      onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                      required
                      data-testid="input-full-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      data-testid="input-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password {editingEmployee && '(leave blank to keep current)'}</Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      required={!editingEmployee}
                      data-testid="input-password"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="designation">Designation</Label>
                    <Input
                      id="designation"
                      value={formData.designation}
                      onChange={(e) => setFormData({...formData, designation: e.target.value})}
                      data-testid="input-designation"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="department">Department</Label>
                    <Input
                      id="department"
                      value={formData.department}
                      onChange={(e) => setFormData({...formData, department: e.target.value})}
                      data-testid="input-department"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="salary">Monthly Salary</Label>
                    <Input
                      id="salary"
                      type="number"
                      value={formData.salary}
                      onChange={(e) => setFormData({...formData, salary: parseFloat(e.target.value)})}
                      data-testid="input-salary"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="date_of_joining">Date of Joining</Label>
                    <Input
                      id="date_of_joining"
                      type="date"
                      value={formData.date_of_joining}
                      onChange={(e) => setFormData({...formData, date_of_joining: e.target.value})}
                      data-testid="input-date-joining"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="weekly_off">Weekly Off Days</Label>
                    <div className="flex flex-wrap gap-2">
                      {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day) => (
                        <label key={day} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.weekly_off.includes(day)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({...formData, weekly_off: [...formData.weekly_off, day]});
                              } else {
                                setFormData({...formData, weekly_off: formData.weekly_off.filter(d => d !== day)});
                              }
                            }}
                            className="rounded border-gray-300"
                            data-testid={`checkbox-${day.toLowerCase()}`}
                          />
                          <span className="text-sm">{day}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="paid_leaves_quota">Paid Leaves Quota</Label>
                    <Input
                      id="paid_leaves_quota"
                      type="number"
                      value={formData.paid_leaves_quota}
                      onChange={(e) => setFormData({...formData, paid_leaves_quota: parseInt(e.target.value)})}
                      data-testid="input-leaves-quota"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="role">Role</Label>
                    <Select 
                      value={formData.role} 
                      onValueChange={(value) => setFormData({...formData, role: value})}
                    >
                      <SelectTrigger data-testid="select-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="employee">Employee</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-4 p-4 bg-secondary rounded-lg">
                  <h3 className="font-semibold">Office Location (For Mobile Geofencing)</h3>
                  <div className="grid grid-cols-1 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="office_address">Office Address</Label>
                      <Input
                        id="office_address"
                        value={formData.office_address}
                        onChange={(e) => setFormData({...formData, office_address: e.target.value})}
                        placeholder="Enter office address"
                        data-testid="input-office-address"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="office_latitude">Latitude</Label>
                        <Input
                          id="office_latitude"
                          type="number"
                          step="any"
                          value={formData.office_latitude}
                          onChange={(e) => setFormData({...formData, office_latitude: parseFloat(e.target.value) || ''})}
                          placeholder="e.g., 28.6139"
                          data-testid="input-office-latitude"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="office_longitude">Longitude</Label>
                        <Input
                          id="office_longitude"
                          type="number"
                          step="any"
                          value={formData.office_longitude}
                          onChange={(e) => setFormData({...formData, office_longitude: parseFloat(e.target.value) || ''})}
                          placeholder="e.g., 77.2090"
                          data-testid="input-office-longitude"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Employees can only mark attendance within 200m of this location via mobile app.
                      Use Google Maps to get coordinates.
                    </p>
                  </div>
                </div>

                <Button type="submit" className="w-full" data-testid="submit-employee-button">
                  {editingEmployee ? 'Update Employee' : 'Add Employee'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card data-testid="employees-table-card">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Users className="mr-2 h-5 w-5" />
              All Employees
            </CardTitle>
            <CardDescription>Complete list of employees</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : employees.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No employees found
              </div>
            ) : (
              <div className="w-full overflow-auto rounded-lg border border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Designation</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Salary</TableHead>
                      <TableHead>Weekly Off</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {employees.map((emp) => (
                      <TableRow key={emp.id} data-testid="employee-row" className={!emp.is_active ? 'opacity-50' : ''}>
                        <TableCell className="font-medium">{emp.full_name}</TableCell>
                        <TableCell>{emp.email}</TableCell>
                        <TableCell>{emp.designation || '-'}</TableCell>
                        <TableCell>{emp.department || '-'}</TableCell>
                        <TableCell>₹{emp.salary?.toFixed(2) || '0.00'}</TableCell>
                        <TableCell>
                          {Array.isArray(emp.weekly_off) 
                            ? emp.weekly_off.join(', ') 
                            : emp.weekly_off || 'Sunday'}
                        </TableCell>
                        <TableCell className="capitalize">{emp.role}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            emp.is_active 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {emp.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEdit(emp)}
                              data-testid="edit-button"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant={emp.is_active ? "secondary" : "default"}
                              size="sm"
                              onClick={() => handleToggleActive(emp.id, emp.is_active, emp.full_name)}
                              data-testid="toggle-active-button"
                            >
                              {emp.is_active ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDelete(emp.id, emp.full_name)}
                              data-testid="delete-button"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
